import { useState } from 'react'
import { usePainelDoCredito, useRevogarCredito } from '../../../api/queries/creditoDaIa'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import { CartaoDoSaldo } from './CartaoDoSaldo'
import { DialogoDeCredito, DialogoDeRevogacao } from './DialogoDeCredito'
import { DialogoDePreco } from './DialogoDePreco'
import { TabelaDePrecos } from './TabelaDePrecos'

/**
 * O crédito de IA do mês — a tela do administrador.
 *
 * **É a única tela do sistema que mostra valor em reais relativo à IA**, e ela existe porque o
 * dono perguntou "qual o teto em reais?" e pediu para poder configurá-lo. As três pernas da V31
 * foram tratadas assim: agora existe preço declarado e datado (a primeira caiu); a volatilidade
 * dele virou o argumento a favor de versionar em vez de motivo para não ter (a segunda caiu); e a
 * terceira — um R$ ao lado da fiscalização é lido como custo do contrato com a contratada —
 * continua inteira, e é por isso que este endereço é de ADMIN e nenhum valor aparece sem o preço e
 * a procedência ao lado.
 *
 * @returns a tela do crédito
 */
export function PaginaCreditoDaIa() {
  const t = TEXTOS.creditoDaIa
  const { data: painel, isPending, isError, error } = usePainelDoCredito()
  const [aberto, setAberto] = useState<'credito' | 'preco' | null>(null)
  const [revogando, setRevogando] = useState<number | null>(null)
  const revogar = useRevogarCredito()

  // Ausente e nulo são a mesma coisa aqui: o servidor omite campo nulo do JSON
  // (`default-property-inclusion: non_null`), e uma comparação com `null` erra os dois.
  const precoVigente = painel?.precos.find((p) => !p.vigenciaFim) ?? null
  const modeloEmUso = painel?.consumo.modelos[0] ?? null

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{t.titulo}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{t.ajuda}</p>
        </div>
        <div className="flex gap-2">
          <Botao aparencia="secundario" onClick={() => setAberto('preco')}>
            {t.preco.novo}
          </Botao>
          <Botao onClick={() => setAberto('credito')} disabled={!painel || Boolean(painel.saldo)}>
            {t.credito.confirmar}
          </Botao>
        </div>
      </header>

      {isPending && <Esqueleto linhas={5} />}
      {isError && <AvisoDeErro erro={error} />}

      {painel && (
        <>
          <CartaoDoSaldo saldo={painel.saldo ?? null} consumo={painel.consumo} precoVigente={precoVigente} />
          <TabelaDePrecos
            precos={painel.precos}
            creditos={painel.creditos}
            hoje={painel.hoje}
            aoRevogar={setRevogando}
          />
        </>
      )}

      {aberto === 'credito' && painel && (
        <DialogoDeCredito competencia={painel.competencia} aoFechar={() => setAberto(null)} />
      )}
      {aberto === 'preco' && <DialogoDePreco modeloEmUso={modeloEmUso} aoFechar={() => setAberto(null)} />}
      {revogando !== null && (
        <DialogoDeRevogacao creditoId={revogando} revogar={revogar} aoFechar={() => setRevogando(null)} />
      )}
    </div>
  )
}
